import React, { FC, useContext, useCallback, useEffect, useRef } from 'react';
// @ts-ignore
import Graph from 'react-graph-vis';

import { palette } from 'styles/muiTheme';
import { LayoutContext } from 'contexts/LayoutContext/LayoutContext';

const filterNodes = (nodesList: any, edgesList: any, nodesData: any, edgesData: any, useDarkTheme: boolean, nodeId?: any) => {
  const relatedNodeIds = edgesList
    .filter(({from, to}: any) => [from, to].includes(nodeId))
    .map(({from, to}: any) => {
      return from === nodeId ? to : from;
    });
  for (const node of nodesList) {
    if (nodesData?.get) {
      const thisNode = nodesData.get(node.id);
      if (thisNode) {
        if (node.id === nodeId) {
          nodesData.update({
            ...thisNode,
            color: palette.primary,
            font: {
              color: useDarkTheme ? '#ececec' : '#172232',
            },
          });
        } else if (node.level === 1) {
          nodesData.update({
            ...thisNode,
            color: palette.primary,
            font: {
              color: useDarkTheme ? '#ececec' : '#172232',
            },
          });
        } else if (relatedNodeIds.includes(node.id)) {
          nodesData.update({
            ...thisNode,
            color: palette.secondary,
            font: {
              color: useDarkTheme ? '#ececec' : '#172232',
            },
          });
        } else {
          nodesData.update({
            ...thisNode,
            color: `${useDarkTheme ? '#ececec' : '#172232'}40`,
            font: {
              color: `${useDarkTheme ? '#ececec' : '#172232'}40`,
            },
          });
        }
      }
    }
  }
};


let internalData: any = null;

export const GraphChart: FC<any> = ({ data, randomSeed = 3, compact = false, onNetworkReady, onScaleChange, forceDark = false }) => {

  const {useDarkTheme: contextDark} = useContext(LayoutContext);
  const useDarkTheme = forceDark || contextDark;
  const networkRef = useRef<any>(null);

  useEffect(() => {
    if (internalData && data) {
      filterNodes(data.nodes, data.edges, internalData.nodes, internalData.edges, useDarkTheme);
    }
  }, [useDarkTheme, data]);

  const onHoverNode = useCallback(({node, ...rest}: any) => {
    if (internalData && data) {
      filterNodes(data.nodes, data.edges, internalData.nodes, internalData.edges, useDarkTheme, node);
    }
  }, [useDarkTheme, data]);

  // Update node images based on zoom level
  const updateNodeVisuals = useCallback((scale: number) => {
    if (!internalData || !data) return;
    const showImages = scale > 0.45;
    for (const node of data.nodes) {
      const current = internalData.nodes.get(node.id);
      if (!current) continue;
      if (node.image) {
        const targetShape = showImages ? 'circularImage' : 'dot';
        if (current.shape !== targetShape) {
          internalData.nodes.update({
            ...current,
            shape: targetShape,
            ...(targetShape === 'circularImage' ? { image: node.image, imagePadding: 5 } : {}),
          });
        }
      }
    }
    if (onScaleChange) onScaleChange(scale);
  }, [data, onScaleChange]);

  const nodeSize = compact ? 12 : 18;
  const fontSize = compact ? 11 : 14;

  const options = {
    layout: {
      randomSeed,
      clusterThreshold: 300,
      hierarchical: {
        enabled: false,
      },
    },
    physics: {
      enabled: true,
      repulsion: {
        centralGravity: 0,
        springLength: compact ? 120 : 200,
        springConstant: 0.05,
        nodeDistance: compact ? 60 : 100,
        damping: 0.09,
      },
      barnesHut: {
        centralGravity: 0.1,
        avoidOverlap: 0,
      },
      maxVelocity: 50,
      minVelocity: 0.1,
      solver: 'barnesHut',
      stabilization: {
        enabled: true,
        iterations: 1000,
        updateInterval: 100,
        onlyDynamicEdges: false,
        fit: true,
      },
      timestep: 0.5,
      adaptiveTimestep: true,
    },
    edges: {
      arrows: {
        to: {
          enabled: false,
        },
      },
      width: 1,
      color: {
        color: `${useDarkTheme ? '#ececec' : '#172232'}60`,
        highlight: palette.primary,
        hover: palette.primary,
      },
      smooth: {
        enabled: true,
        type: "continuous",
        roundness: 0.5,
      },
    },
    interaction: {
      tooltipDelay: 200,
      navigationButtons: false,
      zoomView: false,
      hover: true,
      dragView: true,
      keyboard: {
        enabled: false,
      },
      multiselect: false,
    },
    manipulation: {
      enabled: false,
    },
    nodes: {
      widthConstraint: true,
      size: nodeSize,
      color: {
        border: palette.secondary,
        background: palette.secondary,
        highlight: {
          border: palette.primary,
          background: palette.primary,
        },
        hover: {
          border: palette.secondary,
          background: palette.secondary,
        },
      },
      font: {
        color: useDarkTheme ? '#ececec' : '#172232',
        face: 'itc-avant-garde-gothic-pro, sans-serif',
        size: fontSize,
      },
      shape: 'dot',
    },
  };

  return (
    <Graph
      graph={{
        nodes: data.nodes.map((n: any) => ({
          ...n,
          shape: 'dot',
          size: n.level === 1 ? nodeSize * 1.5 : nodeSize,
          font: {
            color: useDarkTheme ? '#ececec' : '#172232',
            face: 'itc-avant-garde-gothic-pro, sans-serif',
            size: n.level === 1 ? fontSize * 1.3 : fontSize,
          },
        })),
        edges: data.edges,
      }}
      events={{
        hoverNode: onHoverNode,
        zoom: ({ scale }: any) => updateNodeVisuals(scale),
      }}
      options={options}
      getNetwork={(network: any) => {
        //@ts-ignore
        window.network = network;
        networkRef.current = network;
        internalData = network.body.data;
        if (onNetworkReady) onNetworkReady(network);

        // After stabilization, zoom out to show everything
        network.once('stabilizationIterationsDone', () => {
          network.fit({ animation: { duration: 500, easingFunction: 'easeInOutQuad' } });
          setTimeout(() => {
            updateNodeVisuals(network.getScale());
          }, 600);
        });

        // Poll scale after button-driven zoom animations
        network.on('animationFinished', () => {
          updateNodeVisuals(network.getScale());
        });
      }}
    />
  )
}
